/**
 * Billing list state machine - replaces useBillingStore (Zustand) in CRM entity
 */
import { setup, assign } from 'xstate'
import type { BillingAccount, PaymentHistory, ChargeHistory } from '../types'

export interface BillingListContext {
  accounts: Map<string, BillingAccount>
  paymentHistory: Map<string, PaymentHistory[]>
  chargeHistory: Map<string, ChargeHistory[]>
  searchResults: BillingAccount[]
  isLoading: boolean
  error: string | null
}

export type BillingListEvent =
  | { type: 'SET_ACCOUNT'; account: BillingAccount }
  | { type: 'SET_ACCOUNTS'; accounts: BillingAccount[] }
  | { type: 'SET_PAYMENT_HISTORY'; accountNumber: string; history: PaymentHistory[] }
  | { type: 'SET_CHARGE_HISTORY'; accountNumber: string; history: ChargeHistory[] }
  | { type: 'SET_SEARCH_RESULTS'; results: BillingAccount[] }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'CLEAR_SEARCH_RESULTS' }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as BillingListContext,
    events: {} as BillingListEvent,
  },
})

export const billingListMachine = machineSetup.createMachine({
  id: 'billingList',
  context: {
    accounts: new Map(),
    paymentHistory: new Map(),
    chargeHistory: new Map(),
    searchResults: [],
    isLoading: false,
    error: null,
  },
  on: {
    SET_ACCOUNT: {
      actions: assign({
        accounts: ({ context, event }) => {
          if (event.type !== 'SET_ACCOUNT') return context.accounts
          const next = new Map(context.accounts)
          next.set(event.account.accountNumber, event.account)
          return next
        },
      }),
    },
    SET_ACCOUNTS: {
      actions: assign({
        accounts: ({ event }) => {
          if (event.type !== 'SET_ACCOUNTS') return new Map()
          const next = new Map<string, BillingAccount>()
          event.accounts.forEach((acc) => next.set(acc.accountNumber, acc))
          return next
        },
      }),
    },
    SET_PAYMENT_HISTORY: {
      actions: assign({
        paymentHistory: ({ context, event }) => {
          if (event.type !== 'SET_PAYMENT_HISTORY') return context.paymentHistory
          const next = new Map(context.paymentHistory)
          next.set(event.accountNumber, event.history)
          return next
        },
      }),
    },
    SET_CHARGE_HISTORY: {
      actions: assign({
        chargeHistory: ({ context, event }) => {
          if (event.type !== 'SET_CHARGE_HISTORY') return context.chargeHistory
          const next = new Map(context.chargeHistory)
          next.set(event.accountNumber, event.history)
          return next
        },
      }),
    },
    SET_SEARCH_RESULTS: {
      actions: assign({
        searchResults: ({ event }) => (event.type === 'SET_SEARCH_RESULTS' ? event.results : []),
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.isLoading : false),
      }),
    },
    SET_ERROR: {
      actions: assign({
        error: ({ event }) => (event.type === 'SET_ERROR' ? event.error : null),
      }),
    },
    CLEAR_SEARCH_RESULTS: {
      actions: assign({ searchResults: [] }),
    },
    CLEAR: {
      actions: assign({
        accounts: new Map(),
        paymentHistory: new Map(),
        chargeHistory: new Map(),
        searchResults: [],
        isLoading: false,
        error: null,
      }),
    },
  },
})
